import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { DEFAULT_DATE_FORMAT } from '../config';
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

export const formatUnixTimestamp = (timestamp: number, format: string = DEFAULT_DATE_FORMAT): string => {
  console.log({ timestamp });
  if (!Number.isFinite(timestamp)) return '-';
  return dayjs(timestamp * 1000).format(format);
};

/**
 * Format Date objects or date strings (not Unix timestamps)
 * @param date - Date object or date string
 * @param format - The format pattern (default: DEFAULT_DATE_FORMAT)
 * @returns Formatted date string
 */
export const formatDate = (date: Date | string, format: string = DEFAULT_DATE_FORMAT): string => {
  if (!date) return '';
  return dayjs(date).format(format);
};

/**
 * Add days to a date
 * @param date - The base date (timestamp, Date object, or string)
 * @param days - Number of days to add (negative to subtract)
 * @param format - Output format (default: DEFAULT_DATE_FORMAT)
 * @returns Formatted date string
 */
export const addDaysToDate = (date: number | Date | string, days: number, format: string = DEFAULT_DATE_FORMAT): string => {
  if (!date || !Number.isFinite(days)) return '';

  if (typeof date === 'number' && Number.isFinite(date)) {
    return dayjs(date * 1000)
      .add(days, 'day')
      .format(format);
  }

  return dayjs(date).add(days, 'day').format(format);
};

/**
 * Convert a date string or Date object to Unix timestamp (seconds since epoch).
 * @param date - Date object or date string (ISO or parseable by dayjs)
 * @param parseFormat - Optional format string for strict parsing (e.g. 'DD/MM/YYYY')
 * @returns Unix timestamp in seconds, or NaN if invalid
 */
export const dateToUnixTimestamp = (date: Date | string, parseFormat?: string): number => {
  if (!date) return NaN;
  const d = parseFormat ? dayjs(date, parseFormat) : dayjs(date);
  return d.isValid() ? Math.floor(d.valueOf() / 1000) : NaN;
};

/**
 * Get Unix timestamp (seconds) for a date N days from now.
 * @param days - Number of days to add (negative for past)
 * @returns Unix timestamp in seconds
 */
export const unixTimestampDaysFromNow = (days: number): number => dayjs().add(days, 'day').unix();

/**
 * Get Unix timestamp (seconds) for the start of the given day.
 * Accepts a Unix timestamp (seconds), Date object, or date string.
 *
 * @param input - Date value as Unix timestamp (seconds), Date, or string
 * @returns Unix timestamp in seconds at 00:00:00, or NaN if invalid input
 *
 * @example
 * getStartOfTheDay(1712668800); // 1712629800 (example output based on timezone)
 * getStartOfTheDay(new Date('2026-04-14T10:30:00')); // start of that day in seconds
 * getStartOfTheDay('2026-04-14'); // start of that day in seconds
 */
export const getStartOfTheDay = (input: number | Date | string): number => {
  if (input === null || input === undefined || input === '') return NaN;
  const d = typeof input === 'number' && Number.isFinite(input) ? dayjs.unix(input) : dayjs(input);
  return d.isValid() ? d.startOf('day').unix() : NaN;
};

/**
 * Get Unix timestamp (seconds) for the end of the given day.
 * Accepts a Unix timestamp (seconds), Date object, or date string.
 *
 * @param input - Date value as Unix timestamp (seconds), Date, or string
 * @returns Unix timestamp in seconds at 23:59:59, or NaN if invalid input
 *
 * @example
 * getEndOfTheDay(1712668800); // 1712716199 (example output based on timezone)
 * getEndOfTheDay(new Date('2026-04-14T10:30:00')); // end of that day in seconds
 * getEndOfTheDay('2026-04-14'); // end of that day in seconds
 */
export const getEndOfTheDay = (input: number | Date | string): number => {
  if (input === null || input === undefined || input === '') return NaN;
  const d = typeof input === 'number' && Number.isFinite(input) ? dayjs.unix(input) : dayjs(input);
  return d.isValid() ? d.endOf('day').unix() : NaN;
};

/**
 * Calendar month (1–12, Jan=1 … Dec=12) from a Unix timestamp in seconds.
 * @param timestamp - Unix timestamp in seconds
 * @returns Month number 1–12, or NaN when timestamp is invalid
 */
export const getMonthFromUnix = (timestamp: number): number => {
  if (!Number.isFinite(timestamp)) return NaN;
  const d = dayjs.unix(timestamp);
  return d.isValid() ? d.month() + 1 : NaN;
};
